import { useEffect, useRef, useState } from "react"
import { Link, router } from "@inertiajs/react"
import {
  Callout,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Link as RadixLink,
  Table,
  Text,
} from "@radix-ui/themes"
import { LoaderCircle } from "lucide-react"
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts"
import { z } from "zod"

const CHART_COLORS = [
  "#6366f1", // indigo
  "#06b6d4", // cyan
  "#f59e0b", // amber
  "#10b981", // emerald
  "#f43f5e", // rose
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#14b8a6", // teal
  "#f97316", // orange
  "#3b82f6", // blue
  "#84cc16", // lime
  "#a855f7", // purple
  "#ef4444", // red
  "#22d3ee", // light cyan
  "#eab308", // yellow
]

const ProductPointSchema = z.object({
  date: z.string(),
  average_position: z.number().nullish(),
  last_position: z.number().int().nullish(),
  observed_runs_count: z.number().int().nullish(),
  expected_runs_count: z.number().int().nullish(),
})

const ProductTrendSchema = z.object({
  product_id: z.number(),
  name: z.string(),
  website: z.string().nullish(),
  is_tracked: z.boolean(),
  latest_position: z.number().int().nullish(),
  best_position: z.number().int().nullish(),
  points: z.array(ProductPointSchema),
})

const PropsSchema = z.object({
  project: z.object({
    id: z.number(),
    name: z.string(),
  }),
  prompt: z.object({
    id: z.number(),
    title: z.string(),
    text: z.string(),
    is_initial_processing: z.boolean(),
    expected_runs_per_day: z.number().int(),
    missing_rank_position: z.number().int(),
  }),
  analytics: z.object({
    days: z.array(
      z.object({
        date: z.string(),
        label: z.string(),
      })
    ),
    products: z.array(ProductTrendSchema),
    expected_runs_per_day: z.number().int(),
    missing_rank_position: z.number().int(),
  }),
})

type Props = z.infer<typeof PropsSchema>

export default function PromptShow(rawProps: Props) {
  const props = PropsSchema.parse(rawProps)
  const chartContainerRef = useRef<HTMLDivElement | null>(null)
  const [chartWidth, setChartWidth] = useState(0)

  useEffect(() => {
    const element = chartContainerRef.current
    if (!element) return

    const observer = new ResizeObserver((entries) => {
      const nextWidth = Math.floor(entries[0]?.contentRect.width ?? 0)
      setChartWidth(nextWidth)
    })

    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!props.prompt.is_initial_processing) return

    const refreshInterval = window.setInterval(() => {
      router.reload({ only: ["prompt", "analytics"] })
    }, 5000)

    return () => window.clearInterval(refreshInterval)
  }, [props.prompt.is_initial_processing])

  const chartSeries = props.analytics.products.map((product, index) => {
    return {
      key: `product_${product.product_id}`,
      name: product.name,
      color: CHART_COLORS[index % CHART_COLORS.length],
      isTracked: product.is_tracked,
    }
  })

  const chartData = props.analytics.days.map((day, index) => {
    const row: Record<string, number | string | null> = {
      date: day.date,
      label: day.label,
    }

    props.analytics.products.forEach((product) => {
      row[`product_${product.product_id}`] = product.points[index]?.average_position ?? null
    })

    return row
  })

  const averageValues = props.analytics.products
    .flatMap((product) => product.points.map((point) => point.average_position))
    .filter((value): value is number => typeof value === "number")

  const maxAveragePosition = Math.max(
    10,
    averageValues.length > 0 ? Math.ceil(Math.max(...averageValues)) : 10
  )
  const tableDays = [...props.analytics.days].reverse()

  return (
    <Box width="100%">
      <Flex justify="between" align="center" mb="4" gap="3" wrap="wrap">
        <Box>
          <Text as="p" size="2" style={{ color: "var(--gray-11)" }}>
            {props.project.name}
          </Text>
          <Heading size="7">{props.prompt.title}</Heading>
        </Box>
        <Button variant="soft" color="gray" asChild>
          <Link href={`/projects/${props.project.id}`}>Back to project</Link>
        </Button>
      </Flex>

      <Box
        mb="4"
        className="relative overflow-hidden rounded-[var(--radius-3)] border border-[#e8dfa0] bg-[#fff9e0] shadow-sm"
        style={{
          padding: "28px 24px",
          backgroundImage: `linear-gradient(#c8dbbe 0, #c8dbbe 1px, transparent 1px)`,
          backgroundSize: "100% 28px",
          backgroundPosition: "0 0",
        }}
      >
        {/* Red margin line */}
        <Box className="absolute inset-y-0 left-12 w-px bg-[#e89898]" />
        <Text
          as="p"
          className="whitespace-pre-wrap italic font-medium text-center text-[#3a3520] font-serif text-[20px]"
          style={{ lineHeight: "28px" }}
        >
          &ldquo;{props.prompt.text}&rdquo;
        </Text>
      </Box>

      {props.prompt.is_initial_processing ? (
        <Callout.Root color="amber" variant="soft" size="2" mb="4">
          <Callout.Icon>
            <LoaderCircle size={16} className="animate-spin" />
          </Callout.Icon>
          <Callout.Text>
            We’re fetching the first ranking results for this prompt now. This message goes away
            automatically when the first run is complete.
          </Callout.Text>
        </Callout.Root>
      ) : null}

      <Card size="3" mb="4">
        <Flex direction="column" gap="3">
          <Heading size="5">Daily average ranking</Heading>
          <Text as="p" size="2" style={{ color: "var(--gray-11)" }}>
            Lower is better. Gaps indicate insufficient data for that day.
          </Text>

          {props.analytics.products.length === 0 ? (
            <Text as="p" size="2" style={{ color: "var(--gray-11)" }}>
              No ranking data yet. Run the ranking refresh job to populate this chart.
            </Text>
          ) : (
            <Box
              ref={chartContainerRef}
              style={{ width: "100%", minWidth: 0, minHeight: "360px", height: "360px" }}
            >
              {chartWidth > 0 ? (
                <LineChart
                  data={chartData}
                  width={chartWidth}
                  height={360}
                  margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-a5)" />
                  <XAxis dataKey="label" tick={{ fill: "var(--gray-11)", fontSize: 12 }} />
                  <YAxis
                    reversed
                    domain={[1, maxAveragePosition]}
                    allowDecimals={false}
                    tick={{ fill: "var(--gray-11)", fontSize: 12 }}
                  />
                  <RechartsTooltip labelFormatter={(label) => `Date: ${String(label)}`} />
                  <Legend />
                  {chartSeries.map((series) => (
                    <Line
                      key={series.key}
                      type="monotone"
                      dataKey={series.key}
                      name={series.name}
                      stroke={series.color}
                      strokeWidth={series.isTracked ? 3 : 1.5}
                      strokeDasharray={series.isTracked ? undefined : "4 3"}
                      strokeOpacity={1}
                      dot={false}
                      connectNulls={false}
                    />
                  ))}
                </LineChart>
              ) : null}
            </Box>
          )}
        </Flex>
      </Card>

      <Card size="3">
        <Flex direction="column" gap="3">
          <Heading size="5">14-day position table</Heading>
          <Text as="p" size="2" style={{ color: "var(--gray-11)" }}>
            Daily averages, rounded to nearest integer. Blank cells indicate insufficient data.
          </Text>

          {props.analytics.products.length === 0 ? (
            <Text as="p" size="2" style={{ color: "var(--gray-11)" }}>
              No products to display yet.
            </Text>
          ) : (
            <Box style={{ overflowX: "auto" }}>
              <Table.Root variant="surface" size="1">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeaderCell>Product</Table.ColumnHeaderCell>
                    {tableDays.map((day) => (
                      <Table.ColumnHeaderCell key={day.date} justify="center">
                        {day.label}
                      </Table.ColumnHeaderCell>
                    ))}
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {props.analytics.products.map((product) => {
                    const pointsByDate = new Map(product.points.map((point) => [point.date, point]))

                    return (
                      <Table.Row key={product.product_id}>
                        <Table.RowHeaderCell>
                          <Flex direction="column" gap="1" align="start">
                            <Link
                              href={`/projects/${props.project.id}/products/${product.product_id}`}
                            >
                              {product.name}
                            </Link>
                            {product.website ? (
                              <RadixLink
                                href={product.website}
                                target="_blank"
                                rel="noreferrer"
                                size="1"
                                color="gray"
                              >
                                Visit website
                              </RadixLink>
                            ) : null}
                          </Flex>
                        </Table.RowHeaderCell>
                        {tableDays.map((day) => {
                          const point = pointsByDate.get(day.date)

                          return (
                            <Table.Cell key={`${product.product_id}_${day.date}`} align="center">
                              {point?.average_position != null ? (
                                <Text>{point.average_position.toFixed(1)}</Text>
                              ) : (
                                <Text color="gray">-</Text>
                              )}
                            </Table.Cell>
                          )
                        })}
                      </Table.Row>
                    )
                  })}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Flex>
      </Card>
    </Box>
  )
}
